/* eslint-disable no-await-in-loop */
/* eslint-disable require-atomic-updates */
import { ElementSource, Media, Story, Template } from "chq";
import { v4 as uuid } from "uuid";

import { getImageFromStory } from "app/utils";
import imagesSizes from "constants/imagesSizes";

import { Preview } from "@creatomate/preview";

import { ELEMENT_NAMES, IMAGE_SLIDE_DURATION, IMAGE_TYPE, IRC_TEXT_DELAY, IRC_TEXT_DURATION, STORY_TRANSITION_TIME, VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import { flattenMultiImageStories } from "components/stories/video-studio/utils";
import { ImplementorMontageTemplateParams } from "components/stories/video-studio/utils/creatomate/setMontageCustomTemplate";
import findElementByName from "../../utils/findElementByName";

export const createIrcMontageTemplate = async (
  preview: Preview,
  template: Template,
  story: Story,
  storyDuration: number,
  time: number,
  lastItem?: boolean
): Promise<string> => {
  const json = preview.getSource();
  const { creator, media } = story;
  const { mediaType, url } = media;

  if (!json) return "";
  const { elements } = json;
  let newElements = [...elements];

  if (lastItem) {
    const lastMedia: Record<string, any> | undefined = newElements.filter((
      element: Record<string, any>
    ) => (
      element.source?.startsWith("https://")
    )).sort((firstElement: Record<string, any>, secondElement: Record<string, any>) => (
      firstElement.time - secondElement.time
    )).slice(-1)[0];

    // this is to remove the last animation from the last current media,
    // we are adding a new last element
    newElements = newElements.map((element: Record<string, any>) => {
      if (element.id !== lastMedia?.id) return element;
      return { ...element, animations: template?.commonProperties?.media.animations };
    });

    // sync outros
    newElements = newElements.map((element: Record<string, any>) => {
      if (element.name === "Outro - Text" || element.name === "Outro - IRC Logo"
        || element.name === "Outro - Header" || element.name === "Outro - Hashtags"
      ) {
        return {
          ...element,
          time: element.time + storyDuration - STORY_TRANSITION_TIME
        };
      }
      return element;
    });
  }

  const nameOneElement = findElementByName(elements, ELEMENT_NAMES.NAME_1);
  const introHeaderElement = findElementByName(elements, ELEMENT_NAMES.INTRO_HEADER);
  const TIME_TO_DISPLAY = (nameOneElement?.time - introHeaderElement?.duration) || IRC_TEXT_DELAY;
  const ELEMENTS_DURATION = nameOneElement?.duration || IRC_TEXT_DURATION;

  // create story media
  const mediaDefaultProperties = lastItem
    ? template.commonProperties?.lastMedia : template?.commonProperties?.media;
  const elementId = uuid();

  // create media
  newElements.push({
    id: elementId,
    name: `${creator.name}'s ${mediaType}`,
    type: mediaType,
    source: mediaType === "video" ? url : getImageFromStory(imagesSizes.FULL, story),
    duration: storyDuration,
    time,
    ...mediaDefaultProperties
  });

  // create name
  newElements.push({
    id: uuid(),
    text: creator.name,
    time: time + TIME_TO_DISPLAY,
    duration: storyDuration < ELEMENTS_DURATION + TIME_TO_DISPLAY
      ? storyDuration - TIME_TO_DISPLAY : ELEMENTS_DURATION,
    ...template.commonProperties?.Name
  });

  // create title
  newElements.push({
    id: uuid(),
    text: creator.title || "",
    time: time + TIME_TO_DISPLAY,
    duration: storyDuration < ELEMENTS_DURATION + TIME_TO_DISPLAY
      ? storyDuration - TIME_TO_DISPLAY : ELEMENTS_DURATION,
    ...template.commonProperties?.Title
  });

  // create department
  newElements.push({
    id: uuid(),
    text: creator.departments?.[0]?.name || "",
    time: time + TIME_TO_DISPLAY,
    duration: storyDuration < ELEMENTS_DURATION + TIME_TO_DISPLAY
      ? storyDuration - TIME_TO_DISPLAY : ELEMENTS_DURATION,
    ...template.commonProperties?.Department
  });

  // create location
  newElements.push({
    id: uuid(),
    text: creator.location?.name || "",
    time: time + TIME_TO_DISPLAY,
    duration: storyDuration < ELEMENTS_DURATION + TIME_TO_DISPLAY
      ? storyDuration - TIME_TO_DISPLAY : ELEMENTS_DURATION,
    ...template.commonProperties?.Location
  });

  // create small_overlay
  newElements.push({
    id: uuid(),
    time: time + TIME_TO_DISPLAY,
    duration: storyDuration < ELEMENTS_DURATION + TIME_TO_DISPLAY
      ? storyDuration - TIME_TO_DISPLAY : ELEMENTS_DURATION,
    ...template.commonProperties?.Small_Overlay
  });

  // create large_overlay
  newElements.push({
    id: uuid(),
    time: time + TIME_TO_DISPLAY,
    duration: storyDuration < ELEMENTS_DURATION + TIME_TO_DISPLAY
      ? storyDuration - TIME_TO_DISPLAY : ELEMENTS_DURATION,
    ...template.commonProperties?.Large_Overlay
  });

  await preview.setSource({ ...json, elements: newElements });
  return elementId;
};

const IrcMontageTemplate = async ({
  template,
  preview,
  selectedStories,
  onStoryUpdate
}: ImplementorMontageTemplateParams): Promise<void> => {
  const json = preview.getSource();
  const elements = json?.elements;

  const filterElementsByName = (
    elements: ElementSource[] | undefined,
    name: typeof ELEMENT_NAMES[keyof typeof ELEMENT_NAMES]
  ): ElementSource[] => {
    const lowerCaseName = name.toLowerCase();
    return elements?.filter(element => element.name?.toLowerCase() === lowerCaseName) || [];
  };

  const outroIrcLogoElement = findElementByName(elements, ELEMENT_NAMES.OUTRO_IRC_LOGO);
  const nameOneElement = findElementByName(elements, ELEMENT_NAMES.NAME_1);
  const introHeaderElement = findElementByName(elements, ELEMENT_NAMES.INTRO_HEADER);
  const outroHeaderElement = findElementByName(elements, ELEMENT_NAMES.OUTRO_HEADER);
  const outroHashtagsElement = findElementByName(elements, ELEMENT_NAMES.OUTRO_HASHTAGS);
  const storyElement = findElementByName(elements, ELEMENT_NAMES.STORY);
  const laststoryElement = findElementByName(elements, ELEMENT_NAMES.LAST_STORY);
  const outrosElements = filterElementsByName(elements, ELEMENT_NAMES.OUTRO_TEXT);
  const lastStoryAnmations = laststoryElement?.animations;
  let lastStoryId = laststoryElement?.id;

  const TIME_TO_DISPLAY = (nameOneElement?.time - introHeaderElement?.duration);
  const ELEMENTS_DURATION = nameOneElement?.duration;
  const OUTRO_DURATION = (outroHeaderElement?.duration + outroHashtagsElement?.duration);
  const INTRO_DURATION = introHeaderElement?.duration;
  const CALC_STORY_TRANSITION_TIME = ((storyElement?.duration
    + introHeaderElement?.duration) - storyElement?.time);

  const nameElements = [
    findElementByName(elements, ELEMENT_NAMES.NAME_1),
    findElementByName(elements, ELEMENT_NAMES.NAME_2),
    findElementByName(elements, ELEMENT_NAMES.NAME_3)
  ];
  const titleElements = [
    findElementByName(elements, ELEMENT_NAMES.TITLE_1),
    findElementByName(elements, ELEMENT_NAMES.TITLE_2),
    findElementByName(elements, ELEMENT_NAMES.TITLE_3)
  ];
  const departmentElements = [
    findElementByName(elements, ELEMENT_NAMES.DEPARTMENT_1),
    findElementByName(elements, ELEMENT_NAMES.DEPARTMENT_2),
    findElementByName(elements, ELEMENT_NAMES.DEPARTMENT_3)
  ];
  const locationElements = [
    findElementByName(elements, ELEMENT_NAMES.LOCATION_1),
    findElementByName(elements, ELEMENT_NAMES.LOCATION_2),
    findElementByName(elements, ELEMENT_NAMES.LOCATION_3)
  ];
  const smallOverlayElements = [
    findElementByName(elements, ELEMENT_NAMES.SMALL_OVERLAY_1),
    findElementByName(elements, ELEMENT_NAMES.SMALL_OVERLAY_2),
    findElementByName(elements, ELEMENT_NAMES.SMALL_OVERLAY_3)
  ];
  const largeOverlayElements = [
    findElementByName(elements, ELEMENT_NAMES.LARGE_OVERLAY_1),
    findElementByName(elements, ELEMENT_NAMES.LARGE_OVERLAY_2),
    findElementByName(elements, ELEMENT_NAMES.LARGE_OVERLAY_3)
  ];
  const storyVideoElements = [
    findElementByName(elements, ELEMENT_NAMES.FIRST_STORY),
    findElementByName(elements, ELEMENT_NAMES.STORY),
    findElementByName(elements, ELEMENT_NAMES.LAST_STORY)
  ];

  const firstOutroElement: ElementSource | undefined = outrosElements.sort(
    (firstElement: ElementSource, secondElement: ElementSource) => (
      firstElement.time - secondElement.time
    )
  )[0];

  const IRC_OUTRO_WAITING_TIME = firstOutroElement?.time - outroHeaderElement?.time;
  const IRC_TEMPLATE_OUTRO_TIME = firstOutroElement?.time;

  const deleteStoryByIndex = async (index: number): Promise<void> => {
    const json = preview.getSource();
    const { elements } = json;
    let newElements = [...elements];
    newElements = newElements.filter((element: ElementSource) => (
      element.id !== nameElements[index]?.id
      && element.id !== nameElements[index]?.id
      && element.id !== titleElements[index]?.id
      && element.id !== departmentElements[index]?.id
      && element.id !== locationElements[index]?.id
      && element.id !== smallOverlayElements[index]?.id
      && element.id !== largeOverlayElements[index]?.id
      && element.id !== storyVideoElements[index]?.id
    ));
    return preview.setSource({ ...json, elements: newElements });
  };

  const flattenStories = flattenMultiImageStories(selectedStories);
  let modifications = {};
  let durationModifications = {};
  let nameModifications = {};

  let time = INTRO_DURATION;

  for (let index = 0; index < flattenStories.length; index += 1) {
    const story = flattenStories[index];
    const media = story.visuals?.[index] || story.media;
    // eslint-disable-next-line max-len
    const storyDuration = media.mediaType === IMAGE_TYPE ? IMAGE_SLIDE_DURATION : (media.duration || 0);

    if (index < 3) {
      durationModifications = {
        ...durationModifications,
        [`${storyVideoElements[index]?.id}.duration`]: storyDuration
      };
      modifications = {
        ...modifications,
        [`${storyVideoElements[index]?.id}`]: media.mediaType === VIDEO_TYPE ? media.url : getImageFromStory(imagesSizes.FULL, story),
        [`${storyVideoElements[index]?.id}.type`]: media.mediaType,
        [`${storyVideoElements[index]?.id}.time`]: time,

        [`${nameElements[index]?.id}`]: story.creator.name,
        [`${nameElements[index]?.id}.time`]: time + TIME_TO_DISPLAY,

        [`${titleElements[index]?.id}`]: story.creator.title || "",
        [`${titleElements[index]?.id}.time`]: time + TIME_TO_DISPLAY,

        [`${departmentElements[index]?.id}`]: story.creator.departments?.[0]?.name || "",
        [`${departmentElements[index]?.id}.time`]: time + TIME_TO_DISPLAY,

        [`${locationElements[index]?.id}`]: story.creator.location?.name || "",
        [`${locationElements[index]?.id}.time`]: time + TIME_TO_DISPLAY,

        [`${smallOverlayElements[index]?.id}.time`]: time + TIME_TO_DISPLAY,
        [`${largeOverlayElements[index]?.id}.time`]: time + TIME_TO_DISPLAY
      };

      if (storyDuration < (ELEMENTS_DURATION + TIME_TO_DISPLAY)) {
        modifications = {
          ...modifications,
          [`${nameElements[index]?.id}.duration`]: Math.max(storyDuration - TIME_TO_DISPLAY, 0),
          [`${titleElements[index]?.id}.duration`]: Math.max(storyDuration - TIME_TO_DISPLAY, 0),
          [`${departmentElements[index]?.id}.duration`]: Math.max(storyDuration - TIME_TO_DISPLAY, 0),
          [`${locationElements[index]?.id}.duration`]: Math.max(storyDuration - TIME_TO_DISPLAY, 0),
          [`${smallOverlayElements[index]?.id}.duration`]: Math.max(storyDuration - TIME_TO_DISPLAY, 0),
          [`${largeOverlayElements[index]?.id}.duration`]: Math.max(storyDuration - TIME_TO_DISPLAY, 0)
        };
      }
      nameModifications = {
        ...nameModifications,
        [`${storyVideoElements[index]?.id}.name`]: media.mediaType === VIDEO_TYPE ? `${story.creator.name}'s video` : `${story.creator.name}'s image`
      };
      onStoryUpdate({ ...story, creatomateId: storyVideoElements[index]?.id });
    } else {
      const elementId = await createIrcMontageTemplate(
        preview, template, story, storyDuration, time
      );
      onStoryUpdate({ ...story, creatomateId: elementId });

      if (selectedStories.length - 1 === index) lastStoryId = elementId;
    }
    if (selectedStories.length - 1 === index) {
      if (index < 3) {
        modifications = {
          ...modifications,
          [`${storyVideoElements[index]?.id}.animations`]: lastStoryAnmations
        };
      } else {
        modifications = {
          ...modifications,
          [`${lastStoryId}.animations`]: lastStoryAnmations
        };
      }
      // plus STORY_TRANSITION_TIME to cancel the middle transition
      // OUTRO_WAITING_TIME to add the last wait time to start the outro
      time += CALC_STORY_TRANSITION_TIME;
    }
    time += storyDuration - CALC_STORY_TRANSITION_TIME;
  }

  if (lastStoryId !== laststoryElement?.id) {
    // means we need to remove the last animations for the last element in the template
    modifications = {
      ...modifications,
      [`${laststoryElement?.id}.animations`]: template.commonProperties?.media.animations
    };
  }

  if (outroIrcLogoElement) {
    modifications = {
      ...modifications,
      [`${outroIrcLogoElement?.id}.time`]: (outroIrcLogoElement?.time - IRC_TEMPLATE_OUTRO_TIME) + time
    };
  }
  if (outroHeaderElement) {
    modifications = {
      ...modifications,
      [`${outroHeaderElement?.id}.time`]: (outroHeaderElement?.time - IRC_TEMPLATE_OUTRO_TIME) + time
    };
  }
  if (outroHashtagsElement) {
    modifications = {
      ...modifications,
      [`${outroHashtagsElement?.id}.time`]: (outroHashtagsElement?.time - IRC_TEMPLATE_OUTRO_TIME) + time
    };
  }

  if (flattenStories.length < 3) {
    for (let index = flattenStories.length; index < 3; index += 1) {
      await deleteStoryByIndex(index);
    }
  }

  // Plus outro
  modifications = {
    ...modifications,
    duration: time + OUTRO_DURATION
  };

  // sync outros time
  outrosElements.forEach((outro: Record<string, any>) => {
    modifications = {
      ...modifications,
      [`${outro.id}.time`]: time + IRC_OUTRO_WAITING_TIME
    };
    time += outro.duration;
  });

  await preview.applyModifications({ ...modifications, ...durationModifications });
  await preview.applyModifications(nameModifications);
};

export default IrcMontageTemplate;
